import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import InlineTipError from '@/components/inline-tips/InlineTipError'
import { Retry, ArrowUp, ArrowDown } from '@/assets/icons'
import ContentViewerHeader from '@/content-viewer/components/common/ContentViewerHeader'
import MarkdownEditor from './MarkdownEditor'
import { useArtifactMarkdownLoader } from '@/artifact-editor/hooks/useArtifactMarkdownLoader'
import { useArtifactAutoSave } from '@/artifact-editor/hooks/useArtifactAutoSave'
import { useArtifactDiffReview } from '@/artifact-editor/hooks/useArtifactDiffReview'
import { useReferenceInfo } from '@/artifact-editor/hooks/useReferenceInfo'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { v4 as uuidv4 } from 'uuid'
import { useSelector } from 'react-redux'
import type { RootState } from '@/store/store'
import InlineEditPopover from '@/inline-edit/components/InlineEditPopover'
import DiffReviewView from '@/inline-edit/components/DiffReviewView'
import { useInlineEdit } from '@/inline-edit/hooks/useInlineEdit'
import { useDiffReview } from '@/inline-edit/hooks/useDiffReview'
import { kDiffCardGap, kDiffCardMinLeft } from '@/inline-edit/constants/diff-card'
import {
  selectAgentConversationFormCaselawSearchFilter,
  selectAgentConversationFormLRRSearchFilter,
  selectAgentConversationFormFilePaths,
  selectAgentConversationFormUserFilesRag,
} from '@/agent/chat_form/store/selectors'
import { selectAgentConversationMatterId } from '@/agent/conversations/store/selectors'
import { formatCitationsForDisplay } from '@/artifact-editor/utils/citation-helpers'
import { AddTemplateButton } from '../../artifact-templates/components/AddTemplateButton'
import { ApplyingTemplateModal } from '../../artifact-templates/components/ApplyingTemplateModal'
import { SelectTemplateModal, SELECT_TEMPLATE_MODAL_TAB, type SelectTemplateModalInitialTab } from '../../artifact-templates/components/SelectTemplateModal'
import { useArtifactExport } from '@/artifact-templates/hooks/useArtifactExport'
import { kGetFeatureFlagStatus, kFeatureFlags } from '@/constants/constants-feature-flags'

type ArtifactEditorProps = {
  conversationId: string
  title: string
  filePath: string
  gcsBucketName: string
  artifactVersionId: string
}

/**
 * Artifact Editor
 *
 * Orchestrates the artifact editing experience: markdown editor, inline edits,
 * diff review, auto-save, reference markers, and analytics tracking.
 */
export default function ArtifactEditor(props: ArtifactEditorProps) {
  const { conversationId, filePath, title, gcsBucketName, artifactVersionId } = props

  // ── State & refs ──

  const [selectTemplateOpen, setSelectTemplateOpen] = useState(false)
  const [initialSelectTemplateTab, setInitialSelectTemplateTab] = useState<SelectTemplateModalInitialTab>(SELECT_TEMPLATE_MODAL_TAB.MATTER_FILES)
  const editorPanelRef = useRef<HTMLDivElement | null>(null)

  // ── Hooks ──

  const { trackEvent } = useAnalytics()
  const matterId = useSelector((state: RootState) => selectAgentConversationMatterId(state, { conversationId }))

  const {
    template,
    uploadingTemplateName,
    isTemplateApplied,
    isExporting,
    applyTemplateModalOpen,
    applyStatus,
    outputFileId,
    onCancelApply,
    onCloseApplyModal,
    onRetry,
    onSaveWithoutTemplate,
    onDownloadWithoutTemplate,
    onViewFile,
    onDownloadWithTemplate,
    onSave,
    onTemplateUpload,
    onTemplateSelect,
  } = useArtifactExport({
    artifactVersionId,
    matterId,
    title,
    conversationId,
  })

  const { markdownString, isLoading, hasError, retry } = useArtifactMarkdownLoader({ gcsBucketName, filePath })

  // ── Analytics refs ──

  const sessionId = useRef<string>(uuidv4())
  const sessionStartTime = useRef<number>(Date.now())
  const formatActionsUsed = useRef<number>(0)
  const hasTrackedOpened = useRef<boolean>(false)
  const hasTrackedSessionCompleted = useRef<boolean>(false)
  const analyticsSnapshot = useRef({
    artifactVersionId,
    conversationId,
    matterId,
    markdownStringLength: markdownString?.length ?? 0,
    trackEvent,
  })

  // ── Auto-save ──

  const autoSave = useArtifactAutoSave({
    artifactVersionId,
    conversationId,
    sessionId: sessionId.current,
    matterId,
    trackEvent,
  })

  // ── Reference info ──

  const referenceInfoByCitationId = useReferenceInfo(artifactVersionId, conversationId)

  // ── Diff review ──

  const getCurrentMarkdown = useCallback(() => autoSave.lastSavedContent.current || markdownString || '', [markdownString])

  const {
    isReviewing: isReviewingDiff,
    oldMarkdown: diffOldMarkdown,
    newMarkdown: diffNewMarkdown,
    handleAccept: clearDiffReview,
    handleReject: clearDiffReviewOnReject,
  } = useDiffReview({ conversationId, getCurrentMarkdown })

  // ── Research filters ──

  const selectorProps = useMemo(() => ({ conversationId }), [conversationId])
  const caselawSearchFilter = useSelector((state: RootState) => selectAgentConversationFormCaselawSearchFilter(state, selectorProps))
  const lrrSearchFilter = useSelector((state: RootState) => selectAgentConversationFormLRRSearchFilter(state, selectorProps))
  const filePaths = useSelector((state: RootState) => selectAgentConversationFormFilePaths(state, selectorProps))
  const userFilesRag = useSelector((state: RootState) => selectAgentConversationFormUserFilesRag(state, selectorProps))
  const researchFilters = useMemo(
    () => ({ caselawSearchFilter, lrrSearchFilter, userFilesRag, filePaths }),
    [caselawSearchFilter, lrrSearchFilter, userFilesRag, filePaths]
  )

  // ── Inline edit ──

  const {
    highlightDecoration,
    popoverAnchorRect,
    selection: inlineEditSelection,
    selectionRange,
    isSubmitting: isInlineEditSubmitting,
    hasPendingResult,
    submit: submitInlineEdit,
    dismiss: dismissInlineEdit,
    cancelEdit,
    onReviewComplete,
    onInsertComplete,
    getEditorView,
    editorContainerRef,
    scrollContainerRef,
    handleEditorReady,
    handleSelectionChange,
  } = useInlineEdit({ conversationId, artifactVersionId, isReviewingDiff, researchFilters })

  // ── Artifact diff review (composes auto-save + diff review + inline edit) ──

  const { handleAcceptDiff, handleRejectDiff, handleInsertBelow, handleInlineEditSubmitWithSave, highlightVisibility, scrollToHighlight } =
    useArtifactDiffReview({
      conversationId,
      artifactVersionId,
      sessionId: sessionId.current,
      referenceInfoByCitationId,
      editorPanelRef,
      trackEvent,
      performSave: autoSave.performSave,
      setEditedContent: autoSave.setEditedContent,
      pendingContent: autoSave.pendingContent,
      lastSavedContent: autoSave.lastSavedContent,
      isReviewingDiff,
      diffOldMarkdown,
      diffNewMarkdown,
      clearDiffReview,
      clearDiffReviewOnReject,
      getEditorView,
      selectionRange,
      inlineEditSelection,
      submitInlineEdit,
      dismissInlineEdit,
      onReviewComplete,
      onInsertComplete,
      highlightDecoration,
      hasPendingResult,
    })

  // ── Derived values ──

  const combinedEditorRef = useCallback(
    (node: HTMLDivElement | null) => {
      editorContainerRef(node)
      editorPanelRef.current = node
    },
    [editorContainerRef]
  )

  const showDiffOverlay = isReviewingDiff || isInlineEditSubmitting

  const hasSideBySideSpace = (() => {
    const el = editorPanelRef.current
    if (!el) return true
    const rect = el.getBoundingClientRect()
    return rect.left > el.offsetWidth + kDiffCardGap + kDiffCardMinLeft
  })()

  // ── Analytics effects ──

  useEffect(() => {
    analyticsSnapshot.current = {
      artifactVersionId,
      conversationId,
      matterId,
      markdownStringLength: markdownString?.length ?? 0,
      trackEvent,
    }
  }, [artifactVersionId, conversationId, matterId, markdownString?.length, trackEvent])

  useEffect(() => {
    if (artifactVersionId && markdownString && !hasTrackedOpened.current) {
      hasTrackedOpened.current = true
      trackEvent(AnalyticsEvent.ArtifactEditorOpened, {
        artifactId: artifactVersionId,
        artifactVersionId: artifactVersionId,
        conversationId,
        sessionId: sessionId.current,
        matterId: matterId ?? 'none',
        artifactType: 'markdown',
      })
    }
  }, [artifactVersionId, conversationId, markdownString, matterId, trackEvent])

  useEffect(() => {
    const sessionIdAtMount = sessionId.current
    const sessionStartTimeAtMount = sessionStartTime.current

    return () => {
      const {
        artifactVersionId: latestArtifactVersionId,
        conversationId: latestConversationId,
        matterId: latestMatterId,
        markdownStringLength,
        trackEvent: latestTrackEvent,
      } = analyticsSnapshot.current

      if (latestArtifactVersionId && !hasTrackedSessionCompleted.current) {
        hasTrackedSessionCompleted.current = true
        const sessionDuration = Date.now() - sessionStartTimeAtMount
        const finalContentLength = autoSave.pendingContent.current?.length ?? markdownStringLength ?? 0

        latestTrackEvent(AnalyticsEvent.ArtifactEditorSessionCompleted, {
          sessionDuration,
          finalContentLength,
          saveCount: autoSave.saveCount.current,
          formatActionsUsed: formatActionsUsed.current,
          artifactId: latestArtifactVersionId,
          artifactVersionId: latestArtifactVersionId,
          conversationId: latestConversationId,
          sessionId: sessionIdAtMount,
          matterId: latestMatterId ?? 'none',
        })
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render ──

  const templateButton =
    matterId && kGetFeatureFlagStatus(kFeatureFlags.NEW_FILES_UX) && kGetFeatureFlagStatus(kFeatureFlags.ARTIFACT_TEMPLATES) ? (
      <AddTemplateButton
        template={template}
        uploadingName={uploadingTemplateName}
        applied={isTemplateApplied}
        matterId={matterId}
        onUpload={onTemplateUpload}
        onOpenSelectTemplate={(initialTab = SELECT_TEMPLATE_MODAL_TAB.MATTER_FILES) => {
          setInitialSelectTemplateTab(initialTab)
          setSelectTemplateOpen(true)
        }}
        onRemove={() => onTemplateSelect(undefined)}
      />
    ) : null

  return (
    <div className="bg-brand-neutral-50 h-full w-full flex flex-col overflow-hidden">
      <ApplyingTemplateModal
        open={applyTemplateModalOpen}
        outputFileName={`${title}.docx`}
        outputFileId={outputFileId}
        status={applyStatus}
        templateName={template?.name ?? ''}
        successLabel={onViewFile ? 'Saved to Matter Files' : 'Downloaded'}
        onCancel={onCancelApply}
        onSaveWithoutTemplate={onSaveWithoutTemplate}
        onDownloadWithoutTemplate={onDownloadWithoutTemplate}
        onDismiss={onCloseApplyModal}
        onRetry={onRetry}
        onViewFile={onViewFile}
      />
      <SelectTemplateModal
        open={selectTemplateOpen}
        matterId={matterId ?? ''}
        initialTab={initialSelectTemplateTab}
        template={template}
        onClose={() => setSelectTemplateOpen(false)}
        onSelect={(selected) => {
          onTemplateSelect(selected)
          setSelectTemplateOpen(false)
        }}
      />
      <ContentViewerHeader
        conversationId={conversationId}
        loading={isLoading}
        title={title}
        isExporting={isExporting}
        onDownloadWithTemplate={onDownloadWithTemplate}
        onSave={onSave}
      />

      <div className="flex-1 overflow-y-auto w-full mb-3" ref={scrollContainerRef}>
        {!hasError && !isLoading && !markdownString && <div>Could not locate this document.</div>}
        {hasError && (
          <div className="bg-white p-4">
            <div className="mb-4 text-left w-full">{InlineTipError('Failed to load artifact.')}</div>
            <button
              className={
                'flex gap-x-2 items-center rounded-md bg-brand-500 border-[1px] border-brand-500 px-2 py-1 mb-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-400'
              }
              onClick={retry}
            >
              <div>Retry</div>{' '}
              <div className={'w-5 h-5'}>
                <Retry />
              </div>
            </button>
          </div>
        )}
        {!hasError && !isLoading && markdownString && (
          <>
            <div ref={combinedEditorRef} className="relative">
              <MarkdownEditor
                initialMarkdown={autoSave.editedContent || markdownString}
                onChange={autoSave.handleContentChange}
                onBlur={autoSave.handleBlur}
                readOnly={isInlineEditSubmitting || isReviewingDiff}
                showMenu={true}
                saveStatus={autoSave.saveStatus}
                isApplyingEdit={isInlineEditSubmitting}
                onRetry={autoSave.handleRetry}
                onEditorReady={handleEditorReady}
                onSelectionChange={handleSelectionChange}
                artifactId={artifactVersionId}
                artifactVersionId={artifactVersionId}
                conversationId={conversationId}
                sessionId={sessionId.current}
                onFormatAction={() => (formatActionsUsed.current += 1)}
                trackEvent={trackEvent}
                highlightDecoration={highlightDecoration}
                matterId={matterId || ''}
                templateButton={templateButton}
                referenceInfoByCitationId={referenceInfoByCitationId}
              />
              {isReviewingDiff && highlightVisibility && hasSideBySideSpace && (
                <div
                  style={{
                    position: 'fixed',
                    left: highlightVisibility.scrollContainerRect.left,
                    width: highlightVisibility.scrollContainerRect.width,
                    bottom: window.innerHeight - highlightVisibility.scrollContainerRect.bottom + 12,
                    zIndex: 10,
                  }}
                  className="flex justify-center pointer-events-none"
                  data-inline-edit
                >
                  <button
                    onClick={scrollToHighlight}
                    className="pointer-events-auto flex items-center gap-1 px-4 py-2 rounded-md bg-brand-neutral-600 hover:bg-brand-neutral-700 text-white text-sm font-semibold tracking-[-0.15px] leading-5 cursor-pointer transition-colors"
                  >
                    {highlightVisibility.direction === 'above' ? <ArrowUp className="size-[18px]" /> : <ArrowDown className="size-[18px]" />}
                    Jump to changes
                  </button>
                </div>
              )}
              {showDiffOverlay && (
                <DiffReviewView
                  oldMarkdown={diffOldMarkdown}
                  newMarkdown={diffNewMarkdown}
                  onAccept={handleAcceptDiff}
                  onReject={handleRejectDiff}
                  onInsertBelow={handleInsertBelow}
                  editorContainerRef={editorPanelRef}
                  isLoading={isInlineEditSubmitting}
                  onCancelEdit={cancelEdit}
                  preprocessDisplay={(md) => formatCitationsForDisplay(md, referenceInfoByCitationId)}
                />
              )}
            </div>
            {inlineEditSelection && popoverAnchorRect && !isInlineEditSubmitting && !isReviewingDiff && (
              <InlineEditPopover anchorRect={popoverAnchorRect} onSubmit={handleInlineEditSubmitWithSave} onDismiss={dismissInlineEdit} />
            )}
          </>
        )}
      </div>
    </div>
  )
}
